import React, { useState } from 'react';
import { isNil } from 'lodash';
import moment from 'moment';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import DatePickerField, { type PickerDate } from '~/components/core/Molecules/Fields/DatePickerField';
import { DateFormat } from '~/DateTimeUtils';
import type { ClaimMilestonesResponseClaimMilestoneModel as ClaimMilestone } from '~/server_shared/openapi/data-contracts';

import TEXTS from '../texts';

interface ClaimMilestoneEditDialogProps {
  milestone: ClaimMilestone;
  /** Resolves once the day is recorded; closing the dialog is the caller's to do. */
  onSave: (occurredOn: string) => Promise<void>;
  /** Resolves once the recorded day is cleared; closing the dialog is the caller's to do. */
  onClear: () => Promise<void>;
  onClose: () => void;
}

const ClaimMilestoneEditDialog: React.FC<ClaimMilestoneEditDialogProps> = ({ milestone, onSave, onClear, onClose }) => {
  const isRecorded = !isNil(milestone.occurred_on);
  const [date, setDate] = useState<PickerDate>(() =>
    isRecorded ? moment(milestone.occurred_on, DateFormat.ISO) : null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const occurredOn = date?.isValid() ? date.format(DateFormat.ISO) : null;

  const handleSubmit = async (action: () => Promise<void>): Promise<void> => {
    setIsSubmitting(true);
    try {
      await action();
      // No reset on success: the caller closes the dialog, so its buttons stay disabled until it goes.
    } catch {
      // The failure is already reported. The dialog stays open with the day entered, to retry.
      setIsSubmitting(false);
    }
  };

  const footerActions = (
    <div className="flex w-full items-center justify-between gap-12">
      <div>
        {isRecorded ? (
          <Button variant="outlined" color="primary" onClick={() => handleSubmit(onClear)} disabled={isSubmitting}>
            {TEXTS.clear}
          </Button>
        ) : null}
      </div>
      <div className="flex gap-12">
        <Button variant="outlined" color="primary" onClick={onClose} disabled={isSubmitting}>
          {TEXTS.cancel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => occurredOn && handleSubmit(() => onSave(occurredOn))}
          disabled={isSubmitting || isNil(occurredOn)}
        >
          {TEXTS.save}
        </Button>
      </div>
    </div>
  );

  return (
    <CardDialog
      isDialog
      title={milestone.label}
      onClose={onClose}
      preventClose={isSubmitting}
      maxWidth="xs"
      fullWidth
      footerActions={footerActions}
    >
      <DatePickerField
        id="claim-milestone-date"
        label={TEXTS.date}
        // A half-typed day stays an invalid date rather than none, as in the Formik date field, so the
        // picker keeps the typed text instead of resetting it.
        value={date ? date.format(DateFormat.ISO) : null}
        onChange={setDate}
        disabled={isSubmitting}
        fullWidth
      />
    </CardDialog>
  );
};

export default ClaimMilestoneEditDialog;
